import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const DiffCheckerTool = () => {
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [diff, setDiff] = useState([]);

  const compareDiff = () => {
    if (!text1.trim() || !text2.trim()) {
      toast.error('Please enter text in both fields');
      return;
    }

    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    const maxLines = Math.max(lines1.length, lines2.length);
    const result = [];

    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || '';
      const line2 = lines2[i] || '';
      
      if (line1 === line2) {
        result.push({ type: 'same', line1, line2, lineNum: i + 1 });
      } else if (!lines1[i]) {
        result.push({ type: 'added', line1: '', line2, lineNum: i + 1 });
      } else if (!lines2[i]) {
        result.push({ type: 'removed', line1, line2: '', lineNum: i + 1 });
      } else {
        result.push({ type: 'changed', line1, line2, lineNum: i + 1 });
      }
    }

    setDiff(result);
    toast.success('Comparison complete');
  };

  const swap = () => {
    const temp = text1;
    setText1(text2);
    setText2(temp);
    setDiff([]);
  };

  const stats = {
    same: diff.filter(d => d.type === 'same').length,
    added: diff.filter(d => d.type === 'added').length,
    removed: diff.filter(d => d.type === 'removed').length,
    changed: diff.filter(d => d.type === 'changed').length,
  };

  return (
    <div className="space-y-4" data-testid="diff-checker-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Diff Checker</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Original</label>
          <Textarea
            placeholder="Enter original text..."
            value={text1}
            onChange={(e) => setText1(e.target.value)}
            className="min-h-[150px] bg-background/50 font-mono text-sm"
            data-testid="text1-input"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Modified</label>
          <Textarea
            placeholder="Enter modified text..."
            value={text2}
            onChange={(e) => setText2(e.target.value)}
            className="min-h-[150px] bg-background/50 font-mono text-sm"
            data-testid="text2-input"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={compareDiff} className="flex-1" data-testid="compare-btn">
          Compare
        </Button>
        <Button variant="outline" onClick={swap}>
          <ArrowRightLeft className="w-4 h-4" />
        </Button>
      </div>

      {diff.length > 0 && (
        <>
          <div className="flex gap-4 text-sm">
            <span className="text-muted-foreground">{stats.same} same</span>
            <span className="text-emerald-400">+{stats.added} added</span>
            <span className="text-red-400">-{stats.removed} removed</span>
            <span className="text-amber-400">~{stats.changed} changed</span>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {diff.map((d, i) => (
              <div
                key={i}
                className={`p-2 rounded text-sm font-mono flex gap-4 ${
                  d.type === 'same' ? 'bg-muted/30' :
                  d.type === 'added' ? 'bg-emerald-500/10 border-l-2 border-emerald-500' :
                  d.type === 'removed' ? 'bg-red-500/10 border-l-2 border-red-500' :
                  'bg-amber-500/10 border-l-2 border-amber-500'
                }`}
              >
                <span className="text-muted-foreground w-6">{d.lineNum}</span>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <span className={d.type === 'removed' || d.type === 'changed' ? 'text-red-400 line-through' : ''}>
                    {d.line1}
                  </span>
                  <span className={d.type === 'added' || d.type === 'changed' ? 'text-emerald-400' : ''}>
                    {d.line2}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DiffCheckerTool;
